import { Modal } from "antd";
import type { CSSProperties } from "react";
import { backendUrl } from "config";

/**
 * รายการ SKU ที่ดึงสต็อกนี้ไปใช้ในโหมด "เชื่อมกับสต็อกหลัก"
 * มาจาก backend error_data (code = STOCK_LINKED_DEPENDENTS) ตอน ลบ SKU / ลบสินค้า / ปิดติดตามสต็อก /
 * เปลี่ยนเป็นขั้นสูง / ลบสต็อก — ต้องนำออกจากรายการเหล่านี้ก่อนถึงจะทำได้
 */
export type LinkedStockDependent = {
  sku_id: string;
  sku_name: string;
  product_id: string;
  product_name: string;
  quantity: number;
  image_id?: string | null;
  image_url?: string | null;
  /** สต็อกที่ถูกใช้ (ชื่อ SKU/สต็อกมาสเตอร์) — ช่วยตอนลบสินค้าที่มีหลาย SKU */
  source_name?: string;
};

/** URL รูป dependent — ประกอบจาก image_id ก่อน (เส้นที่ใช้ได้จริงในแอป) ไม่มีก็ใช้ image_url */
const resolveDependentImageSrc = (d: LinkedStockDependent): string | undefined => {
  if (d.image_id) return `${backendUrl}images/${d.image_id}`;
  if (d.image_url) return d.image_url;
  return undefined;
};

export const showLinkedStockDependentsModal = (
  title: string,
  description: string,
  dependents: LinkedStockDependent[],
) => {
  const showSource = dependents.some((d) => d.source_name);
  Modal.error({
    title,
    width: showSource ? 680 : 560,
    okText: "รับทราบ",
    content: (
      <div>
        <p style={{ marginBottom: 8 }}>{description}</p>
        <p style={{ marginBottom: 8, fontWeight: 600 }}>
          รายการที่ใช้สต็อกนี้อยู่ ({dependents.length}) — กรุณานำออกจากโหมด "เชื่อมกับสต็อกหลัก" ของรายการเหล่านี้ก่อน
        </p>
        <div style={{ maxHeight: 320, overflowY: "auto", border: "1px solid #f0f0f0", borderRadius: 6 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ position: "sticky", top: 0, background: "#fafafa", zIndex: 1 }}>
                <th style={{ ...DEP_TH, width: 56 }}>รูป</th>
                <th style={{ ...DEP_TH, textAlign: "left" }}>สินค้า / SKU ที่ใช้</th>
                {showSource ? <th style={{ ...DEP_TH, textAlign: "left" }}>ใช้สต็อกของ</th> : null}
                <th style={{ ...DEP_TH, width: 90, textAlign: "right" }}>ใช้ (หน่วย)</th>
              </tr>
            </thead>
            <tbody>
              {dependents.map((d, i) => {
                const src = resolveDependentImageSrc(d);
                return (
                  <tr key={`${d.sku_id}-${i}`} style={{ borderTop: "1px solid #f0f0f0" }}>
                    <td style={{ ...DEP_TD, textAlign: "center" }}>
                      <div style={DEP_THUMB}>
                        {src ? (
                          <img
                            src={src}
                            alt={d.product_name || d.sku_name || ""}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).style.visibility = "hidden";
                            }}
                          />
                        ) : null}
                      </div>
                    </td>
                    <td style={DEP_TD}>
                      <div style={{ fontWeight: 600, wordBreak: "break-word" }}>{d.product_name || "-"}</div>
                      {d.sku_name ? <div style={{ color: "#8c8c8c", fontSize: 12 }}>{d.sku_name}</div> : null}
                    </td>
                    {showSource ? <td style={{ ...DEP_TD, wordBreak: "break-word" }}>{d.source_name || "-"}</td> : null}
                    <td style={{ ...DEP_TD, textAlign: "right", whiteSpace: "nowrap" }}>{d.quantity}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    ),
  });
};

/**
 * error จาก backend เป็น "มีสินค้าอื่นผูกสต็อกนี้อยู่" → เด้ง modal ตาราง แล้วคืน true (caller ไม่ต้องแจ้ง error ซ้ำ)
 * error อื่น → คืน false
 */
export const handleLinkedStockDependentsError = (err: any): boolean => {
  const data = err?.error_data;
  if (data?.code !== "STOCK_LINKED_DEPENDENTS" || !Array.isArray(data.dependents)) return false;
  showLinkedStockDependentsModal(
    data.title || "ไม่สามารถทำรายการได้",
    err?.error_message || err?.message || "ไม่สามารถทำรายการได้ เพราะมีสินค้าอื่นผูกสต็อกนี้ไว้อยู่",
    data.dependents as LinkedStockDependent[],
  );
  return true;
};

/** ข้อความ error จาก backend ({ status: "failed", error_message }) */
export const apiErrorMessage = (err: any, fallback: string): string =>
  err?.error_message || err?.message || fallback;

const DEP_TH: CSSProperties = {
  padding: "8px 10px",
  textAlign: "center",
  fontWeight: 600,
  color: "#595959",
  borderBottom: "1px solid #f0f0f0",
};

const DEP_TD: CSSProperties = {
  padding: "8px 10px",
  verticalAlign: "middle",
};

const DEP_THUMB: CSSProperties = {
  width: 40,
  height: 40,
  margin: "0 auto",
  borderRadius: 6,
  border: "1px solid #f0f0f0",
  background: "#f5f5f5",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
